import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { API_BASE_URL } from '../utils/apiConfig';
import Navbar from '../components/Navbar';
import PageHeader from '../components/PageHeader';
import Footer from '../components/Footer';
import Icons from '../components/Icons';
import { Skeleton } from '../components/ui';

const MailingListPage = () => {
  const navigate = useNavigate();
  const [mailingData, setMailingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    fetchMailingList();
  }, []);

  const fetchMailingList = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/admin/admin-players/mailing-list');
      setMailingData(response.data);
    } catch (err) {
      setError(err.message || 'Failed to fetch mailing list');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!mailingData?.emailsString) return;

    try {
      await navigator.clipboard.writeText(mailingData.emailsString);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const openEmailClient = () => {
    if (mailingData?.mailtoLink) {
      window.location.href = mailingData.mailtoLink;
    }
  };

  const handlePlayerWithoutEmail = (playerName) => {
    // Navigate to player management page with the player name as a query parameter
    navigate(`/admin/manage-players?player=${encodeURIComponent(playerName)}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-navy-900">
      <Navbar />
      <PageHeader title="Player Mailing List" />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-navy-900 dark:border dark:border-gray-800 rounded-lg shadow-md p-6">
          {/* Description Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">League Communication</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              This tool allows admins and moderators to easily communicate with all league players who have
              provided email addresses. You can copy the email list to your clipboard or open your default
              email client with all addresses pre-filled in the TO: field.
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center">
                <Icons.Lock className="w-4 h-4 mr-2" />
                Privacy & Security
              </h3>
              <ul className="text-sm text-blue-700 dark:text-blue-300/80 space-y-1">
                <li>• Email addresses are never displayed on screen for privacy protection</li>
                <li>• Only admins and moderators can access this feature</li>
              </ul>
            </div>
          </div>

          {loading && (
            <div className="space-y-4">
              <Skeleton className="h-6 w-48" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div key={idx} className="p-4 border border-gray-100 dark:border-gray-800 rounded-lg space-y-2 bg-gray-50 dark:bg-navy-800/60">
                    <Skeleton className="h-5 w-1/2" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              <strong>Error:</strong> {error}
            </div>
          )}

          {mailingData && (
            <div>
              {/* Statistics */}
              <div className="bg-gray-50 dark:bg-navy-800/60 rounded-lg p-6 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div className="bg-white dark:bg-navy-900 dark:border dark:border-gray-800 rounded-lg p-4 shadow-sm">
                    <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{mailingData.totalEmails}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Players with Emails</div>
                  </div>
                  <div className="bg-white dark:bg-navy-900 dark:border dark:border-gray-800 rounded-lg p-4 shadow-sm">
                    <div className="text-3xl font-bold text-green-600 dark:text-green-400">{mailingData.players.length}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Players</div>
                  </div>
                  <div className="bg-white dark:bg-navy-900 dark:border dark:border-gray-800 rounded-lg p-4 shadow-sm">
                    <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                      {Math.round((mailingData.totalEmails / mailingData.players.length) * 100)}%
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Email Coverage</div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <button
                  onClick={copyToClipboard}
                  className="bg-blue-500 hover:bg-blue-600 text-white p-6 rounded-lg flex flex-col items-center justify-center transition-colors"
                >
                  <Icons.Copy className="w-8 h-8 mb-3" />
                  <span className="text-lg font-semibold">
                    {copySuccess ? '✓ Copied!' : 'Copy Email List'}
                  </span>
                  <span className="text-sm opacity-90 mt-1">
                    Copy all {mailingData.totalEmails} email addresses to clipboard
                  </span>
                </button>

                <button
                  onClick={openEmailClient}
                  className="bg-green-500 hover:bg-green-600 text-white p-6 rounded-lg flex flex-col items-center justify-center transition-colors"
                >
                  <Icons.Mail className="w-8 h-8 mb-3" />
                  <span className="text-lg font-semibold">Open Email Client</span>
                  <span className="text-sm opacity-90 mt-1">
                    Pre-fill TO: with all email addresses
                  </span>
                </button>
              </div>

              {/* Player List */}
              <div className="border-t dark:border-gray-700 pt-6">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-4">Player Email Status</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                  {mailingData.players.map((player, index) => (
                    <div
                      key={index}
                      onClick={!player.hasEmail ? () => handlePlayerWithoutEmail(player.name) : undefined}
                      className={`p-2 rounded border text-sm ${player.hasEmail
                          ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800'
                          : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors'
                        }`}
                      title={!player.hasEmail ? `Click to add email for ${player.name}` : undefined}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate mr-1">{player.name}</span>
                        {player.hasEmail ? (
                          <Icons.CheckCircle className="w-3 h-3 text-green-600 dark:text-green-400 flex-shrink-0" />
                        ) : (
                          <Icons.Edit className="w-3 h-3 text-red-600 dark:text-red-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                  <Icons.Info className="w-3 h-3 inline mr-1" />
                  Click red entries to add email addresses
                </div>
              </div>

              {/* Usage Instructions */}
              <div className="mt-8 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">Usage Tips</h4>
                <ul className="text-sm text-yellow-700 dark:text-yellow-300/80 space-y-1">
                  <li>• <strong>Copy Email List:</strong> Paste into any email client's TO, CC, or BCC field</li>
                  <li>• <strong>Open Email Client:</strong> Opens with all emails in TO field and "Wankers League Update" subject</li>
                  <li>• <strong>Privacy Tip:</strong> Consider moving emails to BCC after opening to protect player privacy</li>
                  <li>• <strong>Red Player Names:</strong> Click to add missing email addresses</li>
                  <li>• <strong>Subject Suggestions:</strong> "League Update", "Event Reminder", "Season Announcement"</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MailingListPage;
